/**
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('core/constants/action_types');

var apiIndex = require('./index');
var coreModules = require('./core_modules');
var Data = require('../data');
var enums = require('core/constants/enums');

var Analytics = require('core/analytics');
var ApiEvents = require('./api_events');
var AsyncRequest = require('core/utils/async_request');
var createError = require('core/utils/custom_error').create;
var DateEnv = require('core/env/date');
var Directive = require('core/directive');
var ErrorMonitor = require('../error_monitor');
var EventEnv = require('core/event');
var EventEmitter = require('core/event_emitter');
var Flux = require('core/flux');
var JSONEnv = require('core/lib/json');
var Layer = require('core/layer');
var Logger = require('core/utils/logger');
var PluginManager = require('core/plugin_manager');
var Relay = require('core/analytics/relay');
var View = require('core/view');
var Visitor = require('core/visitor');

var di = require('core/di');
var DimensionDataStore = di.get('stores/dimension_data');
var ViewStore = di.get('stores/view');
var ViewDataStore = di.get('stores/view_data');
var VisitorIdStore = di.get('stores/visitor_id');
var LayerDataStore = di.get('stores/layer_data');
var DirectiveStore = di.get('stores/directive');

var EXPERIMENTAL_EDGE_SPA = __EXPERIMENTAL_EDGE_SPA__;
var EXPERIMENTAL_TRACKING_ONLY = __EXPERIMENTAL_TRACKING_ONLY__;
var EXPERIMENTAL_WEB_SDK = __EXPERIMENTAL_WEB_SDK__;
var isEdgeFollowonSnippet = EXPERIMENTAL_EDGE_SPA || EXPERIMENTAL_TRACKING_ONLY;

var SECONDS_IN_DAY = 24 * 60 * 60;
var MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS = 90;

var ApiListenerError = exports.ApiListenerError = createError('ApiListenerError');

/**
 * As you set up tracking in Optimizely, we recommend capturing extra context about events and pages using tags.
 * By capturing the user's context with structured data, you can set up more powerful targeting, richer analytics,
 * and better integrations.
 *
 * For an example, an ecommerce site might track an event every time the user presses an Add to Cart button.
 * This would enable analytics on how often products get added and behavioral targeting to users who've added to cart
 * before. But this tracking is incomplete if you don't know what was added, what else is in the cart, etc.
 *
 * By tracking this additional information, you can create a data layer for your website or app. You can think of the
 * data layer as a structured representation of everything meaningful that the user sees or does. Once you set up this
 * data layer once, you can share the data with many different tools.
 *
 * ## Metrics
 *
 * By default, every event and pageview in Optimizely is counted up. But often, you'll want to measure more
 * sophisticated metrics like revenue. To track those metrics, just use these standard tags.
 *
 * | Tag name | Description                                                    |
 * |----------|----------------------------------------------------------------|
 * | revenue  | (integer) Use this field to track revenue from any event, e.g. a purchase or ad click. Make sure to send it only once for each purchase to avoid double-counting. Revenue should be represented as the number of cents, so $2.50 becomes 250. |
 * | quantity | (integer) The number of items purchased/added/etc.             |
 *
 * **Example**
```js
{
  revenue: 250, // $2.50
  quantity: 2
}
```
 *
 * ## Page
 *
 * These tags are commonly used to identify what's being viewed.
 *
 * | Tag name    | Description                                                    |
 * |-------------|----------------------------------------------------------------|
 * | category    | (string) e.g. "Kitchen"                                        |
 * | subcategory | (string) e.g. "Blenders"                                       |
 * | name        |  The product name, article title, etc. of what's being viewed  |
 *
 * **Example**
```
// Page tracking with tags
window['optimizely'].push({
  type: "page",
  pageName: "product_detail",
  tags: {
    category: "Kitchen",
    subcategory: "Blenders",
  }
});
```
 *
 * ## Search
 *
 * | Tag name       | Description                                          |
 * |----------------|------------------------------------------------------|
 * | query          | (string) The search query used to generate the list  |
 * | sort_method    | (string) The sort method, e.g. "price"               |
 * | sort_direction | (string) ASC or DESC                                 |
 *
 * **Example**
```
// Page tracking with tags
window['optimizely'].push({
  type: "page",
  pageName: "search"
  tags: {
    query: "cheap blenders",
    sort_method: "price",
    sort_direction: "ASC"
  }
});
```
 *
 * ## E-commerce
 *
 * In addition to the tags above, these tags are often useful in e-commerce use cases.
 *
 * | Tag name       | Description                                          |
 * |----------------|------------------------------------------------------|
 * | brand          | (string) The manufacturer of a product               |
 * | price          | (int) The price in cents (e.g. $10 becomes 1000)     |
 * | sku            | (string) The product's unique SKU                    |
 * | color          | (string) If there are variations, the chosen color   |
 * | size           | (string) If there are variations, the chosen size    |
 *
 * **Example**
```
{
  name: "Vitamix ® 750 Professional Blender",
  brand: "Vitamix",
  category: "Kitchen",
  subcategory: "Blenders",
  price: 64900,
  sku: "113757",
  quantity: 2
}
```
 * @typedef {Object} Tags
 */

/**
 * Event args for a custom event
 * @typedef {Object} CustomEventConfig
 * @property {string} [eventType] - "custom" or undefined
 * @property {string} eventName - The "API name" for an event created in Optimizely, e.g. `add_to_cart`
 * @property {module:api/v1~Tags} [config.tags] - A single-level JSON object with event metadata, e.g. the product being purchased.
 * @property {string} [eventId] - ID of event (if tracking a nonexistent event, this is required)
 */

/**
 * Event args for a click event
 * @typedef {Object} ClickEventConfig
 * @property {string} eventType - "click"
 * @property {Event} eventData - Describes the click event
 */

/**
 * Event args for a pageview event
 * @typedef {Object} PageviewEventConfig
 * @property {string} eventType - "pageview"
 * @property {View} eventData - View to activate
 */

/**
 * Event args for a decision event
 * @typedef {Object} DecisionEventConfig
 * @property {string} eventType - "decision"
 * @property {Decision} eventData - Describes the decision
 */

/**
 * The event function captures visitor behavior and additional data. You can track clicks and pageviews in Optimizely
 * without code, but this API call supports tracking other behaviors like watching a video.
 *
 * **Examples**
```js
// Basic tracking
window['optimizely'].push({
  type: "event",
  eventName: "watchedVideo"
});

// Tracking revenue
window['optimizely'].push({
  type: "event",
  eventName: "purchase",
  tags: {
    revenue: 5000,
  }
});

// Additional metadata
window['optimizely'].push({
  type: "event",
  eventName: "purchase",
  tags: {
    revenue: 10000,
    quantity: 2,
    category: "Shoes",
    subcategory: "Sneakers",
  }
});

// Tracking a custom event (when it isn't in the data store)
window['optimizely'].push({
  type: "event",
  eventName: "myNewEvent",
  eventId: "event123"
});

// Tracking a decision
window['optimizely'].push({
  type: "event",
  eventType: "decision",
  eventData: {
    layerId: "campaignId1",
    experimentId: "experiment456",
    variationId: "variation789"
  }
});

// Tracking a click event
window['optimizely'].push({
  type: "event",
  eventType: "click",
  eventData: {
    id: 'clickId1',
    apiName: 'myClickEvent',
    eventFilter: {
      selector: '#hero > .click'
    }
  }
});

// Tracking a pageview event
window['optimizely'].push({
  type: "event",
  eventType: "pageview",
  eventData: {
    id: 'pageId1',
    apiName: 'pageName',
  }
});
```
 *
 * @param {module:api/v1~CustomEventConfig|module:api/v1~ClickEventConfig|module:api/v1~PageviewEventConfig|module:api/v1~DecisionEventConfig} config
 */

exports.event = function(config:import('api/v1').EventConfig|import('api/v1').ClickEventConfig|import('api/v1').PageviewEventConfig|import('api/v1').DecisionEventConfig) {
  var log;
  switch(config.eventType) {
    case 'click':
      log = eventClickLogger(config);
      break;
    case 'decision':
      log = eventDecisionLogger(config);
      break;
    case 'pageview':
      log = eventPageviewLogger(config);
      break;
    case 'custom':
    default:
      log = eventCustomLogger(config);
      break;
  }

  // Not ready to send yet; not activated
  if (!VisitorIdStore.getBucketingId()) {
    Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
      lifecycle: enums.Lifecycle.postActivate,
      cleanupFn: log,
    });
  } else {
    log();
  }
};

/**
 * @param {module:api/v1~CustomEventConfig} config
 * @returns {Function} Function to log the custom event
 * @private
 */
function eventCustomLogger(config:any): Function {
  var event;
  if (config.eventId) {
    event = EventEnv.create(config.eventId, config.eventName, 'custom');
  }

  // Re-evaluate view tags after every event
  // TODO (asa): investigate performance hit
  View.updateAllViewTags();

  return function log() {
    var logEvent = Analytics.trackCustomEvent(config.eventName, config.tags, event, config.properties);
    if (logEvent) {
      Logger.log('API / Tracking custom event:', config.eventName, config.tags);
    } else {
      Logger.log('API / Not tracking custom event:', config.eventName);
    }
  };
}

/**
 * @param  {module:api/v1~ClickEventConfig} config
 * @returns {Function} Function to log the click event
 * @private
 */
function eventClickLogger(config:any): Function {
  var clickEvent;
  if (config.eventData) {
    clickEvent = EventEnv.create(config.eventData.id, config.eventData.apiName, 'click', config.eventData);
  }

  return function log() {
    var logEvent = Analytics.trackClickEvent(clickEvent);
    if (logEvent) {
      Logger.log('API / Tracking click event:', logEvent);
    } else {
      Logger.log('API / Not tracking click event:', logEvent);
    }
  };
}

/**
 * @param  {module:api/v1~DecisionEventConfig} config
 * @returns {Function} Function to log the decision event
 * @private
 */
function eventDecisionLogger(config:any): Function {
  var decisionData = config.eventData;
  var layerState = Layer.createLayerState(decisionData.layerId, decisionData.experimentId, decisionData.variationId,
                                          decisionData.isLayerHoldback, decisionData.isLayerConcluded);
  var layer = Layer.createSingle(decisionData.layerId, decisionData.experimentId, decisionData.variationId);

  return function log() {
    // Note: record the decision in the main store so that it gets picked up by LayerStore.getLayerStatesForAnalytics
    // in Analytics~createDecisionData; other solutions are possible.
    Layer.recordLayerDecision(layerState.layerId, layerState.decisionTicket, layerState.decision);
    Logger.log('API / Tracking decision event:', layerState);
    Analytics.trackDecisionEvent(layerState.decision, layerState.decisionTicket, layer);
  };
}

/**
 * @param  {module:api/v1~PageviewEventConfig} config
 * @returns {Function} Function to log the pageview event
 * @private
 */
function eventPageviewLogger(config:any): Function {
  var view = View.create(config.eventData.id, config.eventData.apiName);
  var viewState = View.createState(view.id);

  return function log() {
    var logEvent = Analytics.trackViewActivation(view, viewState);
    if (logEvent) {
      Logger.log('API / Tracking pageview event:', logEvent);
    } else {
      Logger.log('API / Not tracking pageview event:', logEvent);
    }
  };
}

/**
 * Undocumented API for overriding core/env/default_client_metadata.
 *
 * To be consumed only by the microsnippet (https://github.com/optimizely/edge-services/tree/master/packages/microsnippet)
 * in setting up the follow-on/tracking snippet.
 *
 * @param {Object} config
 * @param {string} [config.clientName] - The client name. Currently, only "js" (default) or "edge".
 * @param {string} [config.clientVersion] - The client version.
 * @param {string[]} config.forceVariationIds - Force variation ids set in the edge-decider, if any.
 */
exports.clientMetadata = function(config:any) {
  if (isEdgeFollowonSnippet || EXPERIMENTAL_WEB_SDK) {
    Flux.dispatch(actionTypes.SET_CLIENT_NAME, config.clientName);
    Flux.dispatch(actionTypes.SET_CLIENT_VERSION, config.clientVersion);
  }

  if (EXPERIMENTAL_EDGE_SPA || EXPERIMENTAL_WEB_SDK) {
    if (config.forceVariationIds) {
      Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
        forceVariationIds: config.forceVariationIds,
      });
    }
  }
};

/**
 * Undocumented API for supplying a _previously-captured_ optimizelyRedirectData cookie value.
 *
 * To be consumed only by the microsnippet (https://github.com/optimizely/edge-services/tree/master/packages/microsnippet)
 * in setting up the follow-on/tracking snippet. This is useful because the tracking snippet is likely to load much more
 * slowly than a regular client-js snippet.
 *
 * (We could also consider exposing this API for customers who load a regular client-js snippet asynchronously.)
 *
 * This is a "low-priority" API because Relay.load must be called after analytics plugins have been initialized,
 * as noted in commit https://github.com/optimizely/client-js/commit/0acb64ed11fb88d2dd7f42cef52a1851bacdf9cf
 *
 * @param {Object} config
 * @param {string} [config.value] - The read value for the optimizelyRedirectData cookie
 */
exports.priorRedirectString = function(config:any) {
  if (isEdgeFollowonSnippet) {
    Relay.load(config.value);
  }
};

/**
 * Undocumented error API, to be consumed only by the microsnippet (https://github.com/optimizely/edge-services/tree/master/packages/microsnippet).
 * @param {Object} config
 * @param {string} config.engine - The name of the client engine. (Currently, only "microsnippet".)
 * @param {Object} config.errorData - error data
 * @param {string} config.errorData.code - numeric representation of error, mappable to human readable string in error_codes.json
 * @param {string} config.errorData.msVersion - version of the microsnippet that generated the error
 * @param {string} config.errorData.requestId - cloudflare requestId (cf-ray header)
 * @param {string} config.errorData.projectId
 * @param {string} config.errorData.snippetKey
 * @param {Array} [config.errorData.args] - optional args as passed to microsnippet logger
 * @param {Object} config.errorData.metadata
 * @param {Error} [config.errorData.metadata.err] - an error instance
 */
exports.microsnippetError = function(config:any) {
  if (isEdgeFollowonSnippet) {

    var error = config.errorData.metadata && config.errorData.metadata.err || {};
    error.name = config.errorData.code;

    var metadata = {
      engine: config.engine,
      msVersion: config.errorData.msVersion,
      requestId: config.errorData.requestId,
      projectId: config.errorData.projectId,
      snippetKey: config.errorData.snippetKey,
      args: config.errorData.args,
    };

    ErrorMonitor.handleError(error, metadata);
  }
};

/**
 * Undocumented RUM API, to be consumed only by the microsnippet (https://github.com/optimizely/edge-services/tree/master/packages/microsnippet).
 * @param {Object} config
 * @param {string} config.type - 'rum'
 * @param {Object} config.eventData - Merged directly into the RumStore
 */
exports.rum = function(config:any) {
  Flux.dispatch(actionTypes.SET_RUM_DATA, config.eventData);
};
/**
* Undocumented API for including edge-decider's initialViewStates.
* This is to be consumed only in the microsnippet. It is used to tell the follow-on snippet the
* view states for evaluated views. Immediate views won't be evaluated again. Non-immediate views
* will be evaluated again and will either be deactivated (initially true -> now false) or activated
* (initially false -> now true) or left alone (initially true/false -> now still true/false).
* **Example**
```
window['optimizely'].push({
    type: 'initialViewStates',
    states: {
      '1': true,
      '2': false,
    },
});
```
* @param {Object} config
* @param {Object} config.states
*/
exports.initialViewStates = function(config:any) {
 var views = _.map(config.states, function(value, key) {
   return { id: key, isActive: value };
 });
 View.registerViews(views);
};

/**
 * The page function tracks the visitor's current context on a website. This context is used for both analytics
 * ("track the number of views to the checkout page") and targeting ("run this experiment on the checkout page").
 *
 * Specifically, pushing a page has two effects:
 *  * Optimizely will track a pageview event for this page, incrementing the count of views to that page in analytics
 *    and recording the event in the user's behavioral profile
 *  * Any experiences targeted to this page will be activated
 *
 * Pages can be created visually in Optimizely with URL targeting, but this API call provides full flexibility for
 * sites with dynamic content or challenging URL patterns.
 *
 * Page information is reset whenever the browser reloads.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

// Basic page tracking
window['optimizely'].push({
  type: "page",
  pageName: "checkout_stage_1"
});

// Page tracking with tags
window['optimizely'].push({
  type: "page",
  pageName: "product_detail",
  tags: {
    category: "Kitchen",
    subcategory: "Blenders",
    price: 64999,
    sku: "113757"
  }
});
```
 *
 * @param {Object} config
 * @param {string} config.pageName - The "API name" for a page created in Optimizely, e.g. `product_detail`
 * @param {module:api/v1~Tags} [config.tags] - A single-level JSON object with metadata about page content, e.g. the product being purchased.
 */
exports.page = function(config:any) {
  var view = ViewDataStore.getByApiName(config.pageName);

  if (!view) {
    // TODO(tyler): allow this?
    throw new Error('Unknown page "' + config.pageName + '"');
  }

  var isActive = config.hasOwnProperty('isActive') ? config.isActive : true;

  var activatePage = function() {
    if (isActive) {
      View.activateViaAPI(view, config.tags);
    } else {
      View.deactivate(view);
      Logger.log('API / Deactivated Page', View.description(view));
    }
  };

  if (VisitorIdStore.getBucketingId()) {
    activatePage();
  } else {
    // Activate after static views activate
    // Note(tyler): this might lead to confusing results if you push isActive: false
    // on a view which has valid static conditions
    // Note(tyler): these events probably won't have visual tags
    Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
      lifecycle: enums.Lifecycle.postViewsActivated,
      cleanupFn: activatePage,
    });
  }
};

/**
 * Tags are contextual metadata about pages events. They identify what a user is looking at, clicking on, or
 * interacting with. For example, you can use tags to describe a product being purchased, an article being read,
 * or a flight being booked.
 *
 * There are three ways to capture this context:
 *  * Directly on an `event`, using the `tags` property.
 *  * Directly on a `page`, using the `tags` property. These tags will be sent along with any event that happens on
 *    that page.
 *  * Finally, you can use this `tags` call directly to add context without activating a page and tracking a pageview.
 *    These tags will automatically be added to all pages activated in the current pageview.
 *
 * **Examples**
```js
// Tracking the current product
window['optimizely'].push({
  type: "tags",
  tags: {
    category: "Kitchen",
    subcategory: "Blenders",
    price: 64999,
    sku: "113757"
  }
});
```
 *
 * @param {Object} config
 * @param {module:api/v1~Tags} config.tags - Tags to set on the currently activated page(s).
 */
exports.tags = function(config:any) {
  View.setGlobalTags(config.tags);
};

/**
 * The user function captures attributes of a user and stores them in a profile that persists across sessions and
 * syncs across devices.
 *
 * These attributes, are persisted in the browser's local storage and can be used for
 * targeting and analysis in experiment results.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

// Attach extra information to an anonymous visitor
window['optimizely'].push({
  type: "user",
  attributes: {
    frequentFlyerStatus: "Gold",
    frequentFlyerMiles: 25600
  }
});

// Remove a attribute value
window['optimizely'].push({
  type: "user",
  attributes: {
    attributeToRemove: null
  }
});
```
 *
 * While publicly, this API is used to set custom attributes, when in an Edge follow-on snippet
 * (when __EXPERIMENTAL_EDGE_SPA__ or __EXPERIMENTAL_TRACKING_ONLY__ is true),
 * you can also use this API to set a unique visitorId using "visitorId" param.
 * This should be used with extreme care because the visitorId has a direct impact on bucketing logic.
 * If it is not sufficiently unique and random, the bucketing logic can be biased.
 * **Examples**
```js
//Internal Only: Set a visitorId
window['optimizely'].push({
  type: "user",
  visitorId: "834092"
});
```
 * Additionally, when in an Edge SPA snippet (when __EXPERIMENTAL_EDGE_SPA__ is true), you can also set these
 * visitor attributes: IP, location, queryParams, url.
 * **Examples**
```js
//Internal Only: Set IP
window['optimizely'].push({
  type: "user",
  IP: "1.1.1.1",
});
```
 *
 * @param {Object} config
 * @param {Object} [config.attributes] - Map of custom attribute IDs to values
 * @param {string=} [config.IP]
 * @param {Object} [config.location]
 * @param {Array.<Array.<string>>} [config.queryParams]
 * @param {string} [config.url]
 * @param {string} [config.visitorId]
 */
exports.user = function(config:any) {
  // NOTE: This is not a publicly supported API
  if (isEdgeFollowonSnippet && config.visitorId) {
    if (!VisitorIdStore.getBucketingId()) {
      // If client not activated, set visitor id in the visitor store
      Logger.log('API / Setting visitor Id for activation:', config.visitorId);
      Flux.dispatch(actionTypes.SET_VISITOR_ID_VIA_API, config.visitorId);
    } else {
      // Client has already activated, so reset the visitorId directly
      Logger.log('API / Setting visitor Id:', config.visitorId);
      Visitor.setId({
        randomId: config.visitorId
      });
    }
  }

  // NOTE: This is not a publicly supported API
  // Set eagerly calculated attributes from the edge-decider
  if (EXPERIMENTAL_EDGE_SPA) {
    _.each(['IP', 'location', 'queryParams', 'url'], function (attributeKey) {
      if (config[attributeKey]) {
        Logger.log('API / Setting', attributeKey, ':', config[attributeKey]);
        setAttributeValue(attributeKey, config[attributeKey], false);
      }
    });
  }

  Logger.log('API / Setting visitor custom attributes:', config.attributes);
  _.each(config.attributes, function(attributeValue, attributeIdOrApiName) {
    // If matching dimension is not found we will use the key name as the attribute name
    var attributeKeyInStore = attributeIdOrApiName;
    var attributeApiName;
    var attributeId;

    // attributeKey may be either ID or apiName
    var attribute =
      DimensionDataStore.getById(attributeIdOrApiName) ||
      DimensionDataStore.getByApiName(attributeIdOrApiName);

    if (attribute) {
      // Reset the key in store to the ID instead of the passed in value
      attributeKeyInStore = attribute.id;
      attributeApiName = attribute.apiName;
      // If the attribute is actually a (legacy) CustomDimension
      // it has a segmentId property which we prefer for its identifier
      attributeId = attribute.segmentId || attribute.id;
    }

    var attributeDescriptor:any = {
      id: attributeId,
      value: attributeValue,
    };

    if (attributeApiName) {
      attributeDescriptor.name = attributeApiName;
    }

    setAttributeValue(attributeKeyInStore, attributeDescriptor, true);
  });
};


exports.ga4User = function (config: any) {
  if (config.ga4UserId) {
    Logger.log('API / Setting ga4 user id in the global store:', config.ga4UserId);
    Flux.dispatch(actionTypes.SET_GA4_USER_ID, config);
  } else {
    Logger.error('API / Argument "ga4UserId" is missing');
  }
};

/**
 * Helper function for User API to set attribute values only when the visitor store has been initialized
 * @param {string} attributeKey
 * @param {Object|string} attributeValue
 * @param {boolean} isCustomAttribute
 */
var setAttributeValue = function(attributeKey:string, attributeValue:any|string, isCustomAttribute:boolean) {
  var attributes = [{
    key: isCustomAttribute ? ['custom', attributeKey] : [attributeKey],
    value: attributeValue,
    metadata: { lastModified: DateEnv.now() },
  }];

  var setAttributeValueNow = function() {
    Flux.dispatch(actionTypes.SET_VISITOR_ATTRIBUTES, {
      attributes: attributes,
    });
  };

  if (VisitorIdStore.getBucketingId()) {
    // If bucketingId is set, then we have initialized visitor store
    setAttributeValueNow();
  } else {
    // Register for when visitor store is initialized
    Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
      lifecycle: enums.Lifecycle.postVisitorProfileLoad,
      cleanupFn: setAttributeValueNow,
    });
  }
};

/**
 * Opt a visitor out of Optimizely tracking. For example, you may want to opt visitors out of Optimizely tracking as
 * part of your site's broader opt-out preferences.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

// Opt out a visitor
window['optimizely'].push({
  type: "optOut"
});

// Opt in a previously opted-out visitor
window['optimizely'].push({
 type: "optOut",
 isOptOut: false,
});
```
 * @param {Object} config
 * @param {boolean} [config.isOptOut]
 */
exports.optOut = function(config:any) {
  var isOptOut = config.hasOwnProperty('isOptOut') ? config.isOptOut : true;
  Directive.setOptOut(isOptOut);
};

/**
 * Specify the number of days before the Optimizely visitor cookies will be set to expire. You must call
 * "setCookieExpiration" prior to loading the Optimizely snippet. The minimum number of days that can be set
 * is 90 (approximately 3 months). For more information on how Optimizely uses cookies, visit our Learning Center.
 *
 * Note: Some Optimizely cookies are re-set every time a visitor comes to the site, which means the expiration
 * period set with this API call will be used each time the cookie is set.
 *
 * **Examples**
```
window['optimizely'] = window['optimizely'] || [];

window['optimizely'].push({
  type: "cookieExpiration",
  cookieExpirationDays: 365 // 1 year
});
```
 * @param {Object} config
 * @param {number} config.cookieExpirationDays
 */
exports.cookieExpiration = function(config:any) {
  var expirationDays = config.cookieExpirationDays;
  if (expirationDays < MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS) {
    Logger.error('Argument "cookieExpirationDays"=', expirationDays, 'less than minimum days:',
                 MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS, ', setting to minimum.');
    expirationDays = MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS;
  }

  Logger.log('API / Setting cookie age to', expirationDays, 'days.');
  Flux.dispatch(actionTypes.SET_COOKIE_AGE, expirationDays * SECONDS_IN_DAY);
};

/**
 * Tell Optimizely X Web whether to automatically extend the expiration period of its cookies.
 * Call this API with isEnabled=false to prevent Optimizely from automatically extending the expiration period of its cookies
 * By default, isEnabled is set to true - customers would use API call to opt out of auto-refreshing cookies
 *
 * **Examples**
```
window['optimizely'] = window['optimizely'] || [];

window['optimizely'].push({
  type: "extendCookieLifetime",
  isEnabled: false
});
```
 * @param {Object} config
 * @param {number} config.isEnabled
 */
exports.extendCookieLifetime = function(config:any) {
  // Default for automatic cookie refresh is true
  config = _.extend({isEnabled: true}, config);
  Logger.log('API / Setting cookie automatic lifetime extension to', config.isEnabled);
  Flux.dispatch(actionTypes.SET_COOKIE_AUTO_REFRESH, config.isEnabled);
};

/**
 * Instruct Optimizely to set its cookies on a specific subdomain instead of the default domain. You must call
 * "setCookieDomain" prior to loading the Optimizely snippet. By default, Optimizely sets its cookies on the domain,
 * in order to work across subdomains.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

window['optimizely'].push({
  type: "cookieDomain",
  cookieDomain: "www.example.com"
});
```
 * In this example, the cookies Optimizely sets will be available only on ".www.example.com", ("www.example.com" and
 * all of its subdomains), rather than on ".example.com", which is the default.
 *
 * @param {Object} config
 * @param {string} config.cookieDomain
 */
exports.cookieDomain = function(config:any) {
  Logger.log('API / Setting cookie domain to', config.cookieDomain);
  Flux.dispatch(actionTypes.SET_COOKIE_DOMAIN, config.cookieDomain);
};

/**
 * Disable Optimizely entirely. You must call "disable" prior to loading the Optimizely snippet.
 *
 * You can also disable just Optimizely tracking calls from being made using the parameter `scope`: "tracking".
 * Note: This will override the force_tracking query parameter.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

// Completely disable
window['optimizely'].push({type: "disable"});

// Disable tracking only
window['optimizely'].push({
  type: "disable",
  scope: "tracking"
});
```
 *
 * @param {Object} config
 * @param {string} [config.scope]
 */
exports.disable = function(config:any) {
  // TODO(tyler): un-disable?
  if (!config.scope) {
    Logger.log('API / Disabling everything');
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      disabled: true,
    });
  } else if (config.scope === 'tracking') {
    Logger.log('API / Disabling tracking');
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      trackingDisabled: true,
    });
  } else {
    throw new Error('Unknown "scope" for disable: ' + config.scope);
  }
};

/**
 * Tell Optimizely to output its log to the browser's console log (default is "OFF").
 *
 * You can also do this by using the query parameter `optimizely_log={level}`.
 *
 * If you set the `level` to the currently-configured log level this function has no effect
 * other than emitting a warning.
 *
 * **Examples**
```js
window['optimizely'] = window['optimizely'] || [];

// All logs
window['optimizely'].push({type: "log"});

// Warning and above
// that have the word plugin in the message
window['optimizely'].push({
  type: "log",
  level: "WARN",
  match: "plugin",
});
```
 *
 * @param {Object} config
 * @param {string} [config.match]
 * @param {string} [config.level] - One of the following:
 * | level | Description       |
 * |-------|-------------------|
 * | OFF   | No logs           |
 * | ERROR | Errors only       |
 * | WARN  | Warning and above |
 * | INFO  | Info and above    |
 * | DEBUG | Debug and above   |
 * |---------------------------|
 *
 */
exports.log = function(config:any) {
  var logLevel = config.level;
  var logMatcher = config.match;

  if (_.isUndefined(logLevel)) {
    logLevel = 'INFO';
  }

  if (_.isUndefined(logMatcher)) {
    logMatcher = '';
  }

  Logger.setLogMatcher(logMatcher);
  Logger.setLogLevel(logLevel);
};

/**
 * Register an API module, and add prefix 'custom/' to its name
 * @param {Object} config
 * @param {string} config.type (always registerModule)
 * @param {string} config.moduleName The name of the module to use with optimizely.get
 * @param {Getter | *} config.module The module that will be returned from optimizely.get('custom/<moduleName>')
 */
exports.registerModule = function(config:any) {
  var name = 'custom/' + config.moduleName;
  if (coreModules[name] || apiIndex.get(name)) {
    throw new Error('Module name "' + name + '" is reserved. Will not be registered as plugin.');
  }
  PluginManager.registerApiModule(name, config.module);
};

/**
 * Load async info for which we have a resolver registered. Intended to be called by external scripts,
 * such as geo4.js, that provide data to the visitor store.
 *
 * @param {Object} config
 * @param {string} config.source
 * @param {Object} config.data
 * @private
 */
exports.dataFromSource = function(config:any) {
  var source = config.source;

  // "Upsert" a request for source...
  AsyncRequest.makeAsyncRequest(source);

  // ...and resolve it
  AsyncRequest.resolveRequest(source, config.data);
};

/**
 * Adds a public event listener
 *
 * **Examples**
```js
// Campaign Decision
// Listener that fires whenever a decision is made for a campaign (Optimizely has chosen an experience to display).
  window['optimizely'].push({
    type: 'addListener',
    filter: {
      type: 'lifecycle',
      name: 'campaignDecided',
    },
    handler: function(data) {
      console.log('Campaign with id:', data.campaign.id, 'was decided.');
    }
  });
// Callback parameter:
   {Object} data
   {Object} data.data
   {Campaign} data.data.campaign
   {DecisionTicket} data.data.decisionTicket
   {CampaignDecision} data.data.decision
   {Array<Audience>} data.data.audiences
   {string} data.name
   {string} data.type
 *
 *
// Page Activation
// Listener that fires whenever a page is activated.
  window['optimizely'].push({
    type: 'addListener',
    filter: {
      type: 'lifecycle',
      name: 'pageActivated',
    },
    handler: function(data) {
      console.log('Page : ', data.data.page.name, 'was activated');
    }
  });
// Callback parameter:
   {Object} data
   {Object} data.data
   {Page} data.data.page
   {string} data.name
   {string} data.type

```
 *
 * @param {Object} config
 * @param {Object?} config.filter filter only certain event type/names from being handled
 * @param {string?} config.filter.type only add listener for events with certain type
 * @param {string?} config.filter.name only add listener for events with certain type/name, if name is specified type
 * is also required
 * @param {Function} config.handler
 * @param {string} config.token user supplied token that can be passed to removeListener(token)
 */
exports.addListener = function(config:any) {
  if (!_.isFunction(config.handler)) {
    throw new Error('A handler function must be supplied');
  }
  config = _.omit(config, 'type');
  // Do not let api pushed event handlers listen to internal events.
  config.publicOnly = true;
  config.emitErrors = true;

  // Trap/wrap errors thrown by the passed handler
  var originalHandler = config.handler;
  config.handler = function(ev) {
    try {
      return originalHandler(ev);
    } catch(e) {
      throw new ApiListenerError(e);
    }
  };
  // Remove config.type which in this case is addListener.
  // We don't want to pass this to EventEmitter.on
  EventEmitter.on(config);
  // We don't return a token here since this is invoked via optimizely.push(..) for which a return value
  // isn't guaranteed.
};

/**
 * Remove an event listener
 * @param {Object} config
 * @param {string} config.token
 * @private
 */
exports.removeListener = function(config:any) {
  if (!config.token) {
    throw new Error('Must supply a token to removeListener');
  }
  EventEmitter.off(config.token);
};

/**
 * Load DATA into the client
 *
 * **Note** if `__EXPERIMENTAL_FREEZE_DATA__` is set, this will freeze elements of `config.data`
 *
 * @param {Object} config
 * @param {ClientData} config.data
 */
exports.load = function(config:any) {
  config.data = _.extend({}, config.data);
  Data.normalizeClientData(config.data);

  Flux.dispatch(actionTypes.DATA_LOADED, {
    data: config.data,
  });
};

/**
 *
 * @param {Object} config
 * @param {string} config.id ID of integration to update settings for
 * @param {IntegrationSettings} config.settings Settings to update
 */
exports.integrationSettings = function(config:any) {
  if (!config.id) {
    throw new Error('id is required');
  }
  if (!config.settings) {
    throw new Error('settings is required');
  }

  Flux.dispatch(actionTypes.SET_INTEGRATION_SETTINGS, _.extend({}, config.settings, {
    id: config.id,
  }));
};

if (__TEST__) {
  exports.MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS = MINIMUM_TRACKING_COOKIE_EXPIRATION_DAYS;
  exports.SECONDS_IN_DAY = SECONDS_IN_DAY;
}

/**
 * Bucket visitor into a given variation. Note: At this time, once an experiment --> variation
 * mapping for a given visitor has been set, it won't be changed unless this method is used again.
 * @param {Object} config
 * @param {string} [config.campaignId] Layer that this variation bucketing applies to
 * @param {string} config.experimentId Experiment this variation bucketing applies to
 * @param {string} [config.variationId] Variation id to bucket visitor into
 * @param {number} [config.variationIndex] Index of the variation to bucket visitor into
 */
exports.bucketVisitor = function(config:any) {
  if (!config.variationId && _.isUndefined(config.variationIndex) || config.variationId && config.variationIndex) {
    throw new Error('One of a variationId or a variationIndex is required.');
  }
  if (!config.experimentId) {
    throw new Error('An experimentId is required.');
  }

  var layer;
  var experiment;
  var layerId = config.campaignId;

  // If no layerId, find it using experimentId
  if (!layerId) {
    layer = LayerDataStore.getLayerByExperimentId(config.experimentId);
    layerId = layer.id;
    if (!layerId) {
      throw new Error('Could not find layer for experiment ' + config.experimentId);
    }
  } else {
    // Make sure the layerId specified exists
    layer = LayerDataStore.get(layerId);
    if (!layer) {
      throw new Error('Could not find layer ' + layerId);
    }
  }

  // Make sure the layer contains the experimentId specified
  experiment = _.find(layer.experiments, {id: config.experimentId});
  if (!experiment) {
    throw new Error('Could not find experiment ' + config.experimentId + ' in layer ' + layerId);
  }

  var variationId = config.variationId;
  if (!_.isUndefined(config.variationIndex)) {
    variationId = experiment.variations[config.variationIndex].id;
    if (!variationId) {
      throw new Error('Could not find variation at index ' + config.variationIndex + ' in experiment ' + config.experimentId);
    }
  } else {
    // Make sure the experimentId contains the variation
    if (!_.find(experiment.variations, {id: variationId})) {
      throw new Error('Cound not find variation ' + variationId + ' in experiment ' + config.experimentId);
    }
  }
  Visitor.updateVariationIdMap(layerId, config.experimentId, variationId);
  if (VisitorIdStore.getBucketingId()) {
    // Can't persist data until we have a bucketingId, because it's used as key for local storage.
    Visitor.persistVariationIdMap();
  }

  // Update contextual MAB map
  Visitor.updateContextualMabMap(layerId, config.experimentId, { variationId });
  if (VisitorIdStore.getBucketingId()) {
    // Can't persist data until we have a bucketingId, because it's used as key for local storage.
    Visitor.persistContextualMabMap();
  }
};

/**
 * Wait for the cross domain iFrame to load and sync before activating the client.
 * @param {Object} config
 * @param {Array.<string>} config.canonicalOrigins
 */
exports.waitForOriginSync = function(config:any) {
  if (!_.isArray(config.canonicalOrigins)) {
    throw new Error('canonicalOrigins must be an array. Got: ' + JSONEnv.stringify(config.canonicalOrigins));
  }
  _.each(config.canonicalOrigins, function(origin) {
    if (!_.isString(origin)) {
      throw new Error('Each item in canonicalOrigins must be a string. Found type ' + (typeof origin));
    }
  });
  Flux.dispatch(actionTypes.XDOMAIN_SET_CANONICAL_ORIGINS, {
    canonicalOrigins: config.canonicalOrigins,
  });
};

/**
 * Disable cross-origin tracking
 * **Example**
```js
window['optimizely'] = window['optimizely'] || [];

window['optimizely'].push({
  type: "disableCrossOrigin"
});
```
*/
exports.disableCrossOrigin = function() {
  Logger.log('API / cross origin tracking is DISABLED');
  Flux.dispatch(actionTypes.XDOMAIN_SET_DISABLED, {disabled: true});
};

/**
 * Trigger a client activation
 *
 * The client "activation" phase is where:
 *
 *  * The visitor's IDs are read from cookies and if updated, data for that visitor is read from storage.
 *  * Immediately activated Pages are activated.
 *  * All other Pages are queued for activation.
 *  * Information about the environment, such as previous behavior, and data from third parties, is fetched.
 *  * A client activation event is tracked and new "activationId" created to distinguish from the prior activation.
 *
 * In this way, calling the "activate" API is analogous to loading Optimizely in a new page view, but without requiring
 * the page to be reloaded. This is especially useful in a Single Page App context, to enable the views of a Single
 * Page App to emulate the behavior of Optimizely in multi-page websites.
 *
 * Note that the activate API is not supported for Edge projects with SPA support on at this time. See CJS-3858.
 *
 * ** Example **
```js
document.addEventListener("app:viewChange", function() {
  // notify Optimizely about the new view
  window.optimizely = window.optimizely || [];
  window.optimizely.push({type: "activate"});
});
```
 */
exports.activate = function() {
  if (DirectiveStore.shouldActivate() && !EXPERIMENTAL_EDGE_SPA) {
    // Deactivate any active views and cleanup their event listeners
    _.forEach(ViewStore.getActiveViewStates(), function(viewState) {
      View.deactivate(ViewDataStore.get(viewState.id));
    });
    Flux.dispatch(actionTypes.RESET_VIEW_STATES);
    ApiEvents.emitActivateEvent();
  } else {
    Logger.debug('Not activating.');
  }
};

/**
 * Trigger sending of events when __EXPERIMENTAL_MANUAL_EVENT_SEND__ is set, or `holdEvents` has been called.
 *
 * ** Example **
 ```js
  window.optimizely = window.optimizely || [];
  window.optimizely.push({type: "sendEvents"});
 ```
 */
exports.sendEvents = function() {
  ApiEvents.emitSendEvents();
};

/**
 * Instruct tracker_optimizely to hold events until `sendEvents` is called
 *
 * ** Example **
```
window.optimizely = window.optimizely || [];
window.optimizely.push({type: "holdEvents"});
```
 */
exports.holdEvents = function() {
  ApiEvents.emitHoldEvents();
};

// Since these default to false, set to true to enable unit testing behavior
if (__TEST__) {
  EXPERIMENTAL_EDGE_SPA = true;
  EXPERIMENTAL_TRACKING_ONLY = true;
  isEdgeFollowonSnippet = true;

  exports.setEdgeSpa = function(edgeSpa) {
    EXPERIMENTAL_EDGE_SPA = edgeSpa;
  };
}
